/**
 * Ce que l'écran dit d'une ligne que le portage n'a pas écrite (re-verdict
 * HUP R1, 20/09/2026). Trois sorts, trois phrases :
 * - issue INCERTAINE (OpenPaye n'a pas répondu) : la ligne reste « portée »,
 *   on vérifie dans OpenPaye avant de ressaisir — la même absence postée deux
 *   fois y fait deux absences (mesuré le 20/09) ;
 * - refus explicite d'OpenPaye ou du rapprochement : le serveur l'a REMISE
 *   « à vérifier », elle pourra être portée à nouveau ;
 * - refus de qualification (ligne libre, sans rubrique…) : à saisir soi-même.
 * Tout dire « à saisir vous-même » faisait retaper une ligne remise « à
 * vérifier », que « Tout porter » reportait ensuite : absence en double.
 */
export interface LigneRestante {
  salarie: string | null;
  raison: string;
  rePortable?: boolean;
  issueIncertaine?: boolean;
}

export function phraseDuSort(r: LigneRestante): string {
  if (r.issueIncertaine) return `${r.raison} (la ligne reste marquée portée)`;
  if (r.rePortable) {
    return `${r.raison} — remise « à vérifier », vous pourrez la porter à nouveau.`;
  }
  return `à saisir vous-même : ${r.raison}`;
}

/**
 * La trace que le serveur pose dans le commentaire d'une ligne dont OpenPaye
 * n'a pas répondu (api `saisie-client.service.ts`, DEBUT_MARQUE_PORTAGE_INCERTAIN).
 * Même texte des deux côtés : ne pas reformuler l'un sans l'autre.
 */
const DEBUT_MARQUE_PORTAGE_INCERTAIN = 'Portage incertain le ';

export function portageIncertain(commentaire: string | null | undefined): string | null {
  const i = commentaire?.indexOf(DEBUT_MARQUE_PORTAGE_INCERTAIN) ?? -1;
  if (i < 0) return null;
  const date = commentaire!.slice(i + DEBUT_MARQUE_PORTAGE_INCERTAIN.length).match(/^\d{2}\/\d{2}\/\d{4}/);
  return date ? date[0] : '';
}

/**
 * L'étiquette d'une ligne traitée : écrite dans OpenPaye (trace du serveur)
 * ≠ issue incertaine (OpenPaye n'a pas répondu : vérifier avant de ressaisir,
 * re-verdict R6) ≠ portée à la main.
 */
export function etiquetteTraitee(ligne: {
  etat: 'a_verifier' | 'portee' | 'ecartee';
  envoyeeOpenpayeLe?: string | null;
  commentaire?: string | null;
}): string {
  if (ligne.etat !== 'portee') return 'écartée';
  if (ligne.envoyeeOpenpayeLe) {
    return `écrite dans OpenPaye le ${new Date(ligne.envoyeeOpenpayeLe).toLocaleDateString('fr-FR')}`;
  }
  const incertain = portageIncertain(ligne.commentaire);
  if (incertain !== null) {
    return `portée — OpenPaye n’a pas répondu${incertain ? ` le ${incertain}` : ''} : vérifiez dans OpenPaye avant de ressaisir`;
  }
  return 'portée en paie (à saisir à la main)';
}
